import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { differenceInDays } from 'date-fns';
import { ref, get } from 'firebase/database';
import { database } from '../config/firebase';
import './TrailPainting.css';

// Tiny SVG placeholder - shows instantly while real image loads
const PAINTING_PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f4efe7" width="400" height="300"/%3E%3C/svg%3E';

// Get seasonal painting based on current date
const getSeasonalPainting = () => {
  const today = new Date();
  const month = today.getMonth() + 1; // 1-12
  const day = today.getDate();

  // Winter: November 20 - March 31
  if ((month === 11 && day >= 20) || month === 12 || month <= 2 || (month === 3 && day <= 31)) {
    return '/painting-winter.jpg';
  }
  // Fall: September 21 - November 19
  if ((month === 9 && day >= 21) || month === 10 || (month === 11 && day < 20)) {
    return '/painting-fall.jpg';
  }
  // Summer: April 1 - September 20
  return '/painting-summer.jpg';
};

const TrailPainting = () => {
  const [daysUntilVisit, setDaysUntilVisit] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    fetchNextVisitDate();
  }, []);

  const fetchNextVisitDate = async () => {
    try {
      const configRef = ref(database, 'config/nextVisitDate');
      const snapshot = await get(configRef);

      if (snapshot.exists()) {
        const visitDate = new Date(snapshot.val());
        calculateProgress(visitDate);
      } else {
        // Default to 60 days from now if not set
        const defaultDate = new Date();
        defaultDate.setDate(defaultDate.getDate() + 60);
        calculateProgress(defaultDate);
      }
    } catch (error) {
      console.error('Error fetching next visit date:', error);
      // Fallback
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 60);
      calculateProgress(defaultDate);
    }
  };

  const calculateProgress = (visitDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
    const visit = new Date(visitDate);
    visit.setHours(0, 0, 0, 0); // Reset to start of day

    const days = differenceInDays(visit, today);
    console.log('Next visit date:', visit.toLocaleDateString(), 'Days until visit:', days);
    setDaysUntilVisit(days);
  };

  const renderDaysText = () => {
    if (daysUntilVisit === null) {
      return 'Loading...';
    }

    if (daysUntilVisit > 0) {
      return (
        <React.Fragment>
          <span className="days-number">{daysUntilVisit}</span>
          <span className="days-text"> days until we see each other</span>
        </React.Fragment>
      );
    }
    return <span className="days-text">We see each other today! ❤️</span>;
  };

  return (
    <div className="trail-painting">
      <motion.div
        className="painting-frame"
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.3 }}
      >
        <div className="painting-image-container">
          {/* Placeholder - shows immediately */}
          {!imageLoaded && (
            <img
              src={PAINTING_PLACEHOLDER}
              alt="Loading..."
              className="painting-background placeholder-painting"
            />
          )}
          {/* Real painting - fades in when loaded */}
          <motion.img
            src={getSeasonalPainting()}
            alt="Trail painting"
            className="painting-background"
            onLoad={() => setImageLoaded(true)}
            style={{
              opacity: imageLoaded ? 1 : 0,
              position: imageLoaded ? 'relative' : 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: imageLoaded ? 1 : 0 }}
            transition={{ duration: 0.8 }}
          />
        </div>

        <div className="painting-label">
          <motion.p
            className="days-counter"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {renderDaysText()}
          </motion.p>
        </div>
      </motion.div>
    </div>
  );
};

export default TrailPainting;
